<template>
  <div class="storage-assignment">
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5 font-weight-bold">Gestión de Listas de Almacenamiento</h2>
            <v-btn color="primary" @click="loadData" prepend-icon="mdi-refresh">
              Actualizar
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Estadísticas -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="warning" size="40" class="mr-3">mdi-package-down</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ pendingLists.length }}</div>
                  <div class="text-subtitle-2 text-grey">Listas Pendientes</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="info" size="40" class="mr-3">mdi-account-hard-hat</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ operators.length }}</div>
                  <div class="text-subtitle-2 text-grey">Operarios Disponibles</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="success" size="40" class="mr-3">mdi-check-circle</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ assignedLists.length }}</div>
                  <div class="text-subtitle-2 text-grey">Listas Asignadas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="primary" size="40" class="mr-3">mdi-play-circle</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ inProgressLists.length }}</div>
                  <div class="text-subtitle-2 text-grey">En Progreso</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Operarios y sus Asignaciones - Movido arriba -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <span class="text-h6">Operarios y Asignaciones de Almacenamiento</span>
            </v-card-title>
            
            <v-card-text>
              <v-expansion-panels>
                <v-expansion-panel v-for="operator in operators" :key="operator.id">
                  <v-expansion-panel-title>
                    <div class="d-flex align-center justify-space-between w-100">
                      <div class="d-flex align-center">
                        <v-avatar color="primary" size="30" class="mr-3">
                          <span class="text-white">{{ operator.username.charAt(0).toUpperCase() }}</span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ operator.username }}</div>
                          <div class="text-caption text-grey">{{ operator.email }}</div>
                        </div>
                      </div>
                      <v-chip 
                        :color="getOperatorStatusColor(operator)" 
                        size="small"
                      >
                        {{ getOperatorTaskCount(operator.id) }} tareas
                      </v-chip>
                    </div>
                  </v-expansion-panel-title>
                  
                  <v-expansion-panel-text>
                    <div v-if="getOperatorTasks(operator.id).length === 0" class="text-center text-grey py-4">
                      Sin tareas de almacenamiento asignadas
                    </div>
                    <div v-else>
                      <v-list density="compact">
                        <v-list-item 
                          v-for="task in getOperatorTasks(operator.id)" 
                          :key="task.id"
                          class="task-item"
                        >
                          <template v-slot:prepend>
                            <v-icon :color="getStatusColor(task.status)">
                              {{ getStatusIcon(task.status) }}
                            </v-icon>
                          </template>
                          
                          <v-list-item-title>{{ task.list_number }}</v-list-item-title>
                          <v-list-item-subtitle>
                            Transacción: {{ task.transaction?.reference }} | {{ task.estimated_time }} min
                          </v-list-item-subtitle>
                          
                          <template v-slot:append>
                            <v-chip :color="getStatusColor(task.status)" size="small">
                              {{ getStatusText(task.status) }}
                            </v-chip>
                          </template>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Listas de Almacenamiento Pendientes - Ahora ocupa todo el ancho -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <span class="text-h6">Listas Pendientes de Asignación</span>
            </v-card-title>
            
            <v-card-text>
              <ResponsiveDataTable
                :headers="pendingHeaders"
                :items="pendingLists"
                :loading="loading"
                class="storage-table"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.priority="{ item }">
                  <v-chip :color="getPriorityColor(item.priority)" size="small">
                    {{ getPriorityText(item.priority) }}
                  </v-chip>
                </template>

                <template v-slot:item.storage_strategy="{ item }">
                  <v-chip color="info" size="small" variant="outlined">
                    {{ getStrategyText(item.storage_strategy) }}
                  </v-chip>
                </template>

                <template v-slot:item.estimated_time="{ item }">
                  {{ item.estimated_time }} min
                </template>

                <template v-slot:item.total_items="{ item }">
                  {{ item.total_items }} items
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    color="primary"
                    size="small"
                    @click="showAssignDialog(item)"
                    prepend-icon="mdi-account-plus"
                  >
                    Asignar
                  </v-btn>
                </template>
              </ResponsiveDataTable>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog de Asignación -->
    <v-dialog v-model="showAssignmentDialog" max-width="500px" persistent>
      <v-card v-if="selectedList">
        <v-card-title>
          <span class="text-h5">Asignar Lista de Almacenamiento</span>
        </v-card-title>
        
        <v-card-text>
          <div class="mb-4">
            <h3>{{ selectedList.list_number }}</h3>
            <p class="text-grey">Transacción: {{ selectedList.transaction?.reference }}</p>
            <p class="text-grey">Tiempo estimado: {{ selectedList.estimated_time }} minutos</p>
            <p class="text-grey">Items: {{ selectedList.total_items }}</p>
            <v-chip :color="getPriorityColor(selectedList.priority)" size="small" class="mr-2">
              {{ getPriorityText(selectedList.priority) }}
            </v-chip>
            <v-chip color="info" size="small" variant="outlined">
              {{ getStrategyText(selectedList.storage_strategy) }}
            </v-chip>
          </div>

          <v-select
            v-model="selectedOperator"
            :items="operators"
            item-title="username"
            item-value="id"
            label="Seleccionar Operario"
            prepend-icon="mdi-account"
            variant="outlined"
            :rules="[v => !!v || 'Debe seleccionar un operario']"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-avatar color="primary" size="30">
                    <span class="text-white">{{ item.raw.username.charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>{{ item.raw.username }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.raw.email }}</v-list-item-subtitle>
                <template v-slot:append>
                  <v-chip size="small" :color="getOperatorStatusColor(item.raw)">
                    {{ getOperatorTaskCount(item.raw.id) }} tareas
                  </v-chip>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeAssignDialog">Cancelar</v-btn>
          <v-btn 
            color="primary" 
            @click="assignList"
            :loading="assigning"
            :disabled="!selectedOperator"
          >
            Asignar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import api from '@/api'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

export default {
  name: 'StorageAssignment',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      loading: false,
      assigning: false,
      storageLists: [],
      operators: [],
      showAssignmentDialog: false,
      selectedList: null,
      selectedOperator: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      pendingHeaders: [
        { title: 'Lista', key: 'list_number', sortable: true },
        { title: 'Transacción', key: 'transaction.reference', sortable: true },
        { title: 'Estrategia', key: 'storage_strategy', sortable: false },
        { title: 'Prioridad', key: 'priority', sortable: true },
        { title: 'Items', key: 'total_items', sortable: true },
        { title: 'Tiempo Est.', key: 'estimated_time', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    pendingLists() {
      return this.storageLists.filter(list => list.status === 'pending')
    },
    assignedLists() {
      return this.storageLists.filter(list => list.status === 'assigned')
    },
    inProgressLists() {
      return this.storageLists.filter(list => list.status === 'in_progress')
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [storageResponse, operatorsResponse] = await Promise.all([
          api.getStorageLists(),
          api.getOperators()
        ])
        
        this.storageLists = storageResponse.data
        this.operators = operatorsResponse.data
      } catch (error) {
        console.error('Error loading data:', error)
        this.showSnackbar('Error al cargar los datos', 'error')
      } finally {
        this.loading = false
      }
    },

    showAssignDialog(list) {
      this.selectedList = list
      this.selectedOperator = null
      this.showAssignmentDialog = true
    },

    closeAssignDialog() {
      this.showAssignmentDialog = false
      this.selectedList = null
      this.selectedOperator = null
    },

    async assignList() {
      if (!this.selectedOperator || !this.selectedList) return

      this.assigning = true
      try {
        await api.assignStorageList(this.selectedList.id, {
          user_id: this.selectedOperator
        })

        this.showSnackbar('Lista asignada exitosamente', 'success')
        this.closeAssignDialog()
        await this.loadData()
      } catch (error) {
        console.error('Error assigning list:', error)
        this.showSnackbar('Error al asignar la lista', 'error')
      } finally {
        this.assigning = false
      }
    },

    getOperatorTasks(operatorId) {
      return this.storageLists.filter(list => 
        list.assigned_user_id === operatorId && 
        ['assigned', 'in_progress'].includes(list.status)
      )
    },

    getOperatorTaskCount(operatorId) {
      return this.getOperatorTasks(operatorId).length
    },

    getOperatorStatusColor(operator) {
      const taskCount = this.getOperatorTaskCount(operator.id)
      if (taskCount === 0) return 'success'
      if (taskCount <= 2) return 'warning'
      return 'error'
    },

    getPriorityColor(priority) {
      const colors = {
        1: 'success',
        2: 'info', 
        3: 'warning',
        4: 'error',
        5: 'error'
      }
      return colors[priority] || 'grey'
    },

    getPriorityText(priority) {
      const texts = {
        1: 'Baja',
        2: 'Normal',
        3: 'Alta', 
        4: 'Urgente',
        5: 'Crítica'
      }
      return texts[priority] || 'Normal'
    },

    getStrategyText(strategy) {
      const strategies = {
        'abc_optimized': 'ABC Optimizado',
        'chaotic_controlled': 'Caótico Controlado',
        'zone_dedicated': 'Zonas Dedicadas',
        'fifo_optimized': 'FIFO Optimizado',
        'space_optimized': 'Espacio Optimizado'
      }
      return strategies[strategy] || strategy
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'warning',
        'assigned': 'info',
        'in_progress': 'primary',
        'completed': 'success'
      }
      return colors[status] || 'grey'
    },

    getStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'assigned': 'Asignado',
        'in_progress': 'En Progreso',
        'completed': 'Completado'
      }
      return texts[status] || status
    },

    getStatusIcon(status) {
      const icons = {
        'pending': 'mdi-clock-outline',
        'assigned': 'mdi-account-check',
        'in_progress': 'mdi-play-circle',
        'completed': 'mdi-check-circle'
      }
      return icons[status] || 'mdi-help-circle'
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>

<style scoped>
.storage-assignment {
  padding: 20px;
}

.stats-card {
  height: 100%;
  transition: transform 0.2s ease-in-out;
}

.stats-card:hover {
  transform: translateY(-2px);
}

.storage-table {
  background-color: transparent;
}

.task-item {
  border-left: 3px solid #e0e0e0;
  margin-bottom: 8px;
  border-radius: 4px;
}

.task-item:hover {
  background-color: #f5f5f5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .storage-assignment {
    padding: 10px;
  }
  
  /* Stats cards más compactos */
  .stats-card .v-card-text {
    padding: 12px !important;
  }
  
  .stats-card .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 0.75rem !important;
  }
  
  /* Expansion panels más compactos */
  .v-expansion-panel-title {
    padding: 12px !important;
  }
  
  .v-expansion-panel-text {
    padding: 8px 12px !important;
  }
}

@media (max-width: 480px) {
  .storage-assignment {
    padding: 8px;
  }
  
  /* Stats cards extra compactos */
  .stats-card .v-card-text {
    padding: 8px !important;
  }
  
  .stats-card .v-icon {
    margin-right: 8px !important;
    font-size: 32px !important;
  }
  
  .stats-card .text-h4 {
    font-size: 1.25rem !important;
  }
}
</style> 